<template>
  <div>
    <CommunityPage
      v-if="asyncData?.post"
      :post="asyncData.post"
      :post-type="postType"
      :related-posts="asyncData.relatedPosts"
      :main-category="asyncData.mainCategory"
      :display-post-type="displayPostType"
    />
  </div>
</template>

<script setup lang="ts">
import { QueryTypes } from '~/server/utils/contentstack';
import { useCloudinary } from '~/composables/cloudinary';
import { fetchCommunityPost } from '~/util/contentstack/community';
import { getAssetPublicId } from '~/util/contentstack/csHelpers';
import { metaGen } from '~/util/metadata';
import { CTSPost, CTSPostType } from '~/types/contentstack';
import { useRootStore } from '~/store';

const { $bus }: any = useNuxtApp();
const route = useRoute();
const rootStore = useRootStore();
const { locale } = useI18n();
const params = route?.params;
const postType = params?.postType as CTSPostType;
const postSlug = params?.post as string;
const displayPostType =
  postType === 'blog' ? 'Blog' : postType === 'event' ? 'Events' : ('Videos' as 'Blog' | 'Events' | 'Videos');

const event = useRequestEvent();
const cloudinary = useCloudinary();

// TODO - try/catch
const { data: asyncData, error: asyncError } = await useAsyncData(async () => {
  const { post, mainCategory, relatedPosts }: any = await fetchCommunityPost({
    postType,
    postQuery: QueryTypes.postQuery,
    slug: postSlug,
    query: {
      locale: locale?.value,
    },
    event,
  });
  if (!post) throw createError({ statusCode: 404, message: 'Page not found' });

  return {
    post,
    mainCategory,
    relatedPosts,
  };
});

useAsyncErrorHandling({
  error: asyncError.value,
  sentryWanted: false,
});

onMounted(() => {
  $bus.emit('pageView', {
    url: document.URL,
  });
});

const cloudinaryId = computed(() => getAssetPublicId(asyncData.value?.post?.featured_image));

// TODO - dynamic rich results depending on post type
function getRichResults(): any {
  // TODO - complete with accurate info
  const blog = asyncData.value?.post as CTSPost;
  const seo = blog?.seo as any;
  return {
    type: 'application/ld+json',
    // Vue Meta 2.1+ for json property directly
    json: {
      '@context': 'https://schema.org/',
      '@type': 'NewsArticle',
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': 'https://google.com/article',
      },
      headline: blog?.display_title || blog?.title,
      image: [
        cloudinary.generateImageUrl(cloudinaryId.value, { width: 500, height: 500 }), // 1:1
        cloudinary.generateImageUrl(cloudinaryId.value, { width: 800, height: 600 }), // 4:3
        cloudinary.generateImageUrl(cloudinaryId.value, { width: 720, height: 576 }), // 16:9
      ],
      description: seo?.page_description || '',
      datePublished: blog?.publish_date,
      dateModified: '2015-02-05T09:20:00+08:00',
      ...(blog?.author && {
        author: {
          '@type': 'Person',
          name: blog.author,
        },
      }),
      publisher: {
        '@type': 'Organization',
        name: rootStore?.brandSettings?.brand_name,
        logo: {
          '@type': 'ImageObject',
          url: 'https://google.com/logo.jpg',
          width: 600,
          height: 60,
        },
      },
    },
  };
}
const metaGenCustom = metaGen(rootStore?.brandSettings, {
  ...(asyncData.value?.post?.seo || {}),
  ...(cloudinaryId?.value && {
    ogImage: cloudinary.generateImageUrl(cloudinaryId?.value, { width: 800, height: 600 }),
  }),
}) as any;
const blog = getRichResults();
metaGenCustom.script.push(blog);
useHead({
  ...metaGenCustom,
});
</script>
